import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import { fmtMoeda, fmtData } from '@/pages/plan/shared';

interface Invoice {
  id: string;
  numero: string;
  descricao: string;
  valor: number;
  dataVencimento: string;
  dataPagamento: string | null;
  status: string;
  metodo: string;
  invoicePdf: string | null;
  hostedUrl: string | null;
}

interface Resumo {
  totalPago: number;
  totalPendente: number;
  pagas: number;
  pendentes: number;
}

const statusCobranca: Record<string, { label: string; cor: string }> = {
  pago: { label: 'Pago', cor: 'bg-green-50 text-green-700 border-green-200' },
  pendente: { label: 'Pendente', cor: 'bg-amber-50 text-amber-700 border-amber-200' },
  atrasado: { label: 'Atrasado', cor: 'bg-red-50 text-red-700 border-red-200' },
  cancelado: {
    label: 'Cancelado',
    cor: 'bg-background-100 text-foreground-500 border-background-200',
  },
};

export default function CobrancasPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stripeNotConfigured, setStripeNotConfigured] = useState(false);
  const [noStripeCustomer, setNoStripeCustomer] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');

  const checkStripeSetup = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) return false;

      const { data: profile } = await supabase
        .from('profiles')
        .select('stripe_customer_id')
        .eq('id', userId)
        .maybeSingle();

      return !!profile?.stripe_customer_id;
    } catch {
      return false;
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    setStripeNotConfigured(false);
    setNoStripeCustomer(false);

    const hasCustomer = await checkStripeSetup();
    if (!hasCustomer) {
      setNoStripeCustomer(true);
      setLoading(false);
      return;
    }

    try {
      const { data, error: fnError } = await supabase.functions.invoke('get-invoices', {});

      if (fnError) {
        const errMsg = fnError.message || '';
        if (
          errMsg.toLowerCase().includes('stripe') &&
          errMsg.toLowerCase().includes('configurado')
        ) {
          setStripeNotConfigured(true);
        } else if (
          errMsg.toLowerCase().includes('cobranca') &&
          errMsg.toLowerCase().includes('contrate')
        ) {
          setNoStripeCustomer(true);
        } else {
          setError(errMsg || 'Erro ao carregar faturas.');
        }
        setLoading(false);
        return;
      }

      if (data?.error) {
        const errMsg = data.error;
        if (
          errMsg.toLowerCase().includes('stripe') &&
          errMsg.toLowerCase().includes('configurado')
        ) {
          setStripeNotConfigured(true);
        } else if (
          errMsg.toLowerCase().includes('cobranca') &&
          errMsg.toLowerCase().includes('contrate')
        ) {
          setNoStripeCustomer(true);
        } else {
          setError(errMsg);
        }
        setLoading(false);
        return;
      }

      setInvoices(data.invoices || []);
      setResumo(data.resumo || null);
    } catch (err: any) {
      const errMsg = err?.message || '';
      if (errMsg.toLowerCase().includes('stripe') && errMsg.toLowerCase().includes('configurado')) {
        setStripeNotConfigured(true);
      } else {
        setError(errMsg || 'Erro ao carregar faturas.');
      }
    } finally {
      setLoading(false);
    }
  }, [checkStripeSetup]);

  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  const filtradas = invoices.filter((c) => {
    const matchStatus = filtroStatus ? c.status === filtroStatus : true;
    const matchBusca =
      c.descricao.toLowerCase().includes(busca.toLowerCase()) ||
      c.numero.toLowerCase().includes(busca.toLowerCase());
    return matchStatus && matchBusca;
  });

  const totalFiltrado = filtradas.reduce((s, c) => s + c.valor, 0);

  const handleDownload = (invoice: Invoice) => {
    if (invoice.invoicePdf) {
      window.open(invoice.invoicePdf, '_blank');
    } else if (invoice.hostedUrl) {
      window.open(invoice.hostedUrl, '_blank');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Cobrancas</h1>
          <p className="text-sm text-foreground-500 mt-1">
            Historico de faturas e pagamentos do seu plano.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          {/* Loading */}
          {loading && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 flex items-center justify-center">
              <div className="text-center">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
                <p className="text-sm text-foreground-500 mt-3">Carregando faturas...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl text-red-500"></i>
              </div>
              <p className="text-sm font-medium text-foreground-900 mb-2">
                Erro ao carregar faturas
              </p>
              <p className="text-sm text-foreground-500 mb-4">{error}</p>
              <button
                onClick={fetchInvoices}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Tentar novamente
              </button>
            </div>
          )}

          {/* Stripe not configured */}
          {!loading && !error && stripeNotConfigured && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-settings-3-line text-amber-600 text-xs"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Integracao com Stripe nao configurada
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  As faturas serao exibidas aqui quando a integracao com o Stripe estiver ativa.
                  Entre em contato com o suporte para configurar.
                </p>
              </div>
            </div>
          )}

          {/* No Stripe customer */}
          {!loading && !error && noStripeCustomer && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900 mb-2">
                Nenhuma fatura encontrada
              </p>
              <p className="text-sm text-foreground-500 mb-6">
                Voce ainda nao contratou um plano pago. Assim que fizer isso, suas faturas
                aparecerao aqui.
              </p>
              <a
                href="/register"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-add-circle-line"></i>
                Escolher um plano
              </a>
            </div>
          )}

          {/* Content */}
          {!loading && !error && !stripeNotConfigured && !noStripeCustomer && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  {
                    label: 'Total pago',
                    valor: fmtMoeda(resumo?.totalPago || 0),
                    cor: 'bg-green-50 border-green-200',
                    texto: 'text-green-700',
                    icone: 'ri-check-double-line',
                    iconCor: 'text-green-600',
                  },
                  {
                    label: 'Pendente',
                    valor: fmtMoeda(resumo?.totalPendente || 0),
                    cor: 'bg-amber-50 border-amber-200',
                    texto: 'text-amber-700',
                    icone: 'ri-time-line',
                    iconCor: 'text-amber-600',
                  },
                  {
                    label: 'Faturas pagas',
                    valor: `${resumo?.pagas || 0}`,
                    cor: 'bg-background-50 border-background-200/70',
                    texto: 'text-foreground-900',
                    icone: 'ri-file-list-3-line',
                    iconCor: 'text-foreground-500',
                  },
                  {
                    label: 'Faturas pendentes',
                    valor: `${resumo?.pendentes || 0}`,
                    cor: 'bg-background-50 border-background-200/70',
                    texto: 'text-foreground-900',
                    icone: 'ri-file-warning-line',
                    iconCor: 'text-foreground-500',
                  },
                ].map((card) => (
                  <div key={card.label} className={`${card.cor} border rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${card.icone} ${card.iconCor} text-sm`}></i>
                      </div>
                      <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                    </div>
                    <p className={`text-xl font-bold ${card.texto}`}>{card.valor}</p>
                  </div>
                ))}
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 max-w-sm">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <i className="ri-search-line text-foreground-400 text-sm"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por descricao ou numero..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  />
                </div>
                <select
                  value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}
                  className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                >
                  <option value="">Todos os status</option>
                  <option value="pago">Pago</option>
                  <option value="pendente">Pendente</option>
                  <option value="atrasado">Atrasado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>

              {/* Table */}
              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-background-200/70 bg-background-100/50">
                        <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                          Fatura
                        </th>
                        <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                          Descricao
                        </th>
                        <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                          Vencimento
                        </th>
                        <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                          Pagamento
                        </th>
                        <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                          Valor
                        </th>
                        <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                          Status
                        </th>
                        <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                          Acoes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200/50">
                      {filtradas.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-12 text-center text-sm text-foreground-400"
                          >
                            {invoices.length === 0
                              ? 'Nenhuma fatura encontrada. Assim que seu plano gerar cobrancas, elas aparecerao aqui.'
                              : 'Nenhuma fatura corresponde aos filtros.'}
                          </td>
                        </tr>
                      ) : (
                        filtradas.map((cob) => {
                          const stInfo = statusCobranca[cob.status] || statusCobranca.pendente;
                          return (
                            <tr
                              key={cob.id}
                              className="hover:bg-background-100/30 transition-colors"
                            >
                              <td className="px-4 py-3">
                                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                                  {cob.numero}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="max-w-[280px]">
                                  <p className="text-sm text-foreground-700 truncate">
                                    {cob.descricao}
                                  </p>
                                  <p className="text-xs text-foreground-500">{cob.metodo}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm text-foreground-600 whitespace-nowrap">
                                  {fmtData(cob.dataVencimento)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm text-foreground-600 whitespace-nowrap">
                                  {cob.dataPagamento ? fmtData(cob.dataPagamento) : '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm font-semibold text-foreground-800">
                                  {fmtMoeda(cob.valor)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${stInfo.cor}`}
                                >
                                  {stInfo.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {(cob.invoicePdf || cob.hostedUrl) && (
                                  <button
                                    onClick={() => handleDownload(cob)}
                                    className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors ml-auto"
                                    title="Baixar fatura"
                                  >
                                    <i className="ri-download-line text-sm"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {filtradas.length > 0 && (
                      <tfoot>
                        <tr className="border-t border-background-200/70 bg-background-100/30">
                          <td
                            colSpan={4}
                            className="px-4 py-3 text-sm font-semibold text-foreground-700 text-right"
                          >
                            Total exibido:
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-bold text-foreground-900">
                              {fmtMoeda(totalFiltrado)}
                            </span>
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
